import React, { useMemo } from 'react';
import {
  formatNumEU,
  formatPct,
  signedNum,
  getTimeframeLabel,
} from '../dummyData';
import { HiddenValue, useValueHide } from '../ValueHideContext';
import { SubscriptionSummary } from '../../../../types/subscription';
import { SummaryInformation } from '../../types';
import { OverviewChartTimeframe, OverviewChartType } from '../../types';
import SummaryChart from '../../Summary/Chart';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectOverviewChartTimeframe,
  selectOverviewChartType,
  setOverviewChartTimeframe,
  setOverviewChartType,
} from '../../../../store/slices/ui';
import { selectActiveCluster } from '../../../../store/slices/subscription';
import './index.css';

const TIMEFRAMES = Object.values(OverviewChartTimeframe);

// ── Eye toggle (inline next to label) ──────────────────────────────────────

const EyeToggle: React.FC = () => {
  const { hidden, toggle } = useValueHide();
  return (
    <button
      className="hero-eye-toggle"
      onClick={toggle}
      title={hidden ? 'Show values' : 'Hide values'}
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        {hidden ? (
          <>
            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
            <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
            <line x1="1" y1="1" x2="23" y2="23" />
          </>
        ) : (
          <>
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
            <circle cx="12" cy="12" r="3" />
          </>
        )}
      </svg>
    </button>
  );
};

// ── Hero Component ──────────────────────────────────────────────────────────

interface HeroProps {
  subscriptions: SubscriptionSummary[];
  totalValue: number;
  summaryInformation: SummaryInformation | undefined;
  setSummaryInformation: React.Dispatch<
    React.SetStateAction<SummaryInformation | undefined>
  >;
}

const Hero: React.FC<HeroProps> = ({
  subscriptions,
  totalValue,
  summaryInformation,
  setSummaryInformation,
}) => {
  const dispatch = useDispatch();
  const activeCluster = useSelector(selectActiveCluster);
  const overviewChartTimeframe = useSelector(selectOverviewChartTimeframe);
  const overviewChartType = useSelector(selectOverviewChartType);

  const clusterName = activeCluster === null ? 'Portfolio' : activeCluster;

  const costBasis = useMemo(
    () =>
      subscriptions.reduce(
        (acc, s) => acc + (s.profits?.costBasis.stable || 0),
        0,
      ),
    [subscriptions],
  );

  const totalChangeAbs = totalValue - costBasis;
  const totalChangeRel =
    costBasis !== 0 ? (totalValue - costBasis) / costBasis : 0;
  const profit = totalChangeRel >= 0;

  const dayChangeAbs = summaryInformation?.dayChange ?? 0;
  const dayChangeRel = summaryInformation?.dayChangeRelative ?? 0;
  const dayProfit = dayChangeRel >= 0;

  const napDifference = summaryInformation?.napDifference ?? 0;
  const outperformance = napDifference;
  const outperfPositive = outperformance >= 0;

  const currentStableAbs = useMemo(
    () => subscriptions.reduce((acc, s) => acc + s.stableBalance, 0),
    [subscriptions],
  );
  const currentStableRel = totalValue !== 0 ? currentStableAbs / totalValue : 0;

  const amountOfDays = summaryInformation?.amountOfDays || 1;
  const avgDailyAbs = totalChangeAbs / amountOfDays;
  const avgDailyRel = totalChangeRel / amountOfDays;

  const feesPaid = useMemo(
    () =>
      subscriptions.reduce(
        (acc, s) => acc + (s.latestTransaction?.totalFee ?? 0),
        0,
      ),
    [subscriptions],
  );

  const gradientColor = profit ? 'rgba(0,176,97,.18)' : 'rgba(240,22,47,.18)';

  const chartModeLabel =
    overviewChartType === OverviewChartType.FullBalance ? 'full' : 'profit';

  return (
    <div
      className="hero-container"
      style={{
        background: `linear-gradient(180deg, ${gradientColor} 0%, rgba(0,0,0,0) 70%)`,
      }}
    >
      {/* ── Top row ── */}
      <div className="hero-top">
        {/* Left: label + value + change + 24h */}
        <div className="hero-title-section">
          <div className="hero-label-row">
            <p className="hero-label">{clusterName}</p>
            <EyeToggle />
          </div>

          <h1 className="hero-value">
            <HiddenValue width={180}>${formatNumEU(totalValue)}</HiddenValue>
          </h1>

          {/* Profit since */}
          <div className="hero-change">
            <span
              className={`hero-change-amount ${
                profit ? 'color-positive' : 'color-negative'
              }`}
            >
              <HiddenValue width={80}>{signedNum(totalChangeAbs)}</HiddenValue>
            </span>
            <span
              className={`hero-change-pill ${
                profit ? 'bg-positive' : 'bg-negative'
              }`}
            >
              {formatPct(totalChangeRel)}
            </span>
            <span className="hero-change-since">
              {getTimeframeLabel(overviewChartTimeframe)}
            </span>
          </div>

          {/* 24h change */}
          <div className="hero-change hero-change--24h">
            <span
              className={`hero-change-amount ${
                dayProfit ? 'color-positive' : 'color-negative'
              }`}
            >
              <HiddenValue width={60}>{signedNum(dayChangeAbs)}</HiddenValue>
            </span>
            <span
              className={`hero-change-pill ${
                dayProfit ? 'bg-positive' : 'bg-negative'
              }`}
            >
              {formatPct(dayChangeRel)}
            </span>
            <span className="hero-change-since">since yesterday</span>
          </div>
        </div>

        {/* Right: toggles only */}
        <div className="hero-stats-section">
          {/* Timeframe pills */}
          <div className="hero-pill-row">
            {TIMEFRAMES.map((tf) => (
              <button
                key={tf}
                className={`hero-pill${
                  overviewChartTimeframe === tf ? ' hero-pill--active' : ''
                }`}
                onClick={() => dispatch(setOverviewChartTimeframe(tf))}
              >
                {tf}
              </button>
            ))}
          </div>

          {/* Chart mode toggle */}
          <div className="hero-pill-row">
            <button
              className={`hero-pill${
                chartModeLabel === 'full' ? ' hero-pill--active' : ''
              }`}
              onClick={() =>
                dispatch(setOverviewChartType(OverviewChartType.FullBalance))
              }
            >
              Full Balance
            </button>
            <button
              className={`hero-pill${
                chartModeLabel === 'profit' ? ' hero-pill--active' : ''
              }`}
              onClick={() =>
                dispatch(setOverviewChartType(OverviewChartType.ProfitOnly))
              }
            >
              Profit Only
            </button>
          </div>
        </div>
      </div>

      {/* ── Chart area ── */}
      <div className="hero-chart-wrapper">
        <div className="hero-chart-inner">
          <SummaryChart
            subscriptions={subscriptions}
            setSummaryInformation={setSummaryInformation}
            totalValue={totalValue}
          />

          {/* Stats overlaid at bottom of chart, above x-axis */}
          <div className="hero-overlaid-stats">
            {/* Outperformance */}
            <div className="hero-stat-col">
              <span className="hero-stat-col-label">Outperformance</span>
              <p
                className={`hero-stat-col-value ${
                  outperfPositive ? 'color-positive' : 'color-negative'
                }`}
              >
                <HiddenValue width={80}>
                  {signedNum(outperformance)}
                </HiddenValue>{' '}
                <span className="hero-stat-col-pct">
                  {totalValue !== 0
                    ? formatPct(outperformance / totalValue)
                    : ''}
                </span>
              </p>
            </div>

            {/* Current Stable */}
            <div className="hero-stat-col">
              <span className="hero-stat-col-label">Current Stable</span>
              <p className="hero-stat-col-value color-neutral">
                <HiddenValue width={60}>
                  ${formatNumEU(currentStableAbs)}
                </HiddenValue>{' '}
                <span className="hero-stat-col-pct">
                  {(currentStableRel * 100).toFixed(1)}%
                </span>
              </p>
            </div>

            {/* Avg Daily */}
            <div className="hero-stat-col">
              <span className="hero-stat-col-label">Avg Daily</span>
              <p
                className={`hero-stat-col-value ${
                  avgDailyRel >= 0 ? 'color-positive' : 'color-negative'
                }`}
              >
                <HiddenValue width={60}>{signedNum(avgDailyAbs)}</HiddenValue>{' '}
                <span className="hero-stat-col-pct">
                  {formatPct(avgDailyRel)}
                </span>
              </p>
            </div>

            {/* Fees Paid */}
            <div className="hero-stat-col">
              <span className="hero-stat-col-label">Fees Paid</span>
              <p className="hero-stat-col-value color-neutral">
                <HiddenValue width={60}>${formatNumEU(feesPaid)}</HiddenValue>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Hero;
